import { Key } from "lucide-react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import Usuario from "../../model/Usuario";
import UsuarioService from "../../service/administracao/UsuarioService";
import { useToastContext } from "../../provider/ToastContext";
import ContaPagar from "../../model/contaPagar/ContaPagar";
import AntecipacaoParcelaService from "../../service/financeiro/AntecipacaoParcelaService";
import { useState } from "react";
import SimularAntecipacaoRequest from "../../model/antecipacao/SimularAntecipacaoRequest";
import ParcelaSimulacaoAntecipacaoSiengeResponse from "../../model/sienge/antecipacaoParcela/ParcelaSimulacaoAntecipacaoSiengeResponse";
import ParcelaAntecipacaoSienge from "../../model/sienge/antecipacaoParcela/ParcelaAntecipacaoSienge";
import SimulacaoAntecipacaoSiengeResponse from "../../model/sienge/antecipacaoParcela/SimulacaoAntecipacaoSiengeResponse";
import { Calendar } from "primereact/calendar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { localDateStringToDate } from "../../utils/convert";
import SolicitarAntecipacaoRequest from "../../model/antecipacao/SolicitarAntecipacaoRequest";
import { useLoadingContext } from "../../provider/LoadingProvider";

type Props = {
    visible: boolean;
    onHide: () => void;
    contasPagar: ContaPagar[];
    onConfirm: () => void;
}

const antecipacaoParcelaService = new AntecipacaoParcelaService();
export default function SimulacaoAntecipacaoDialog({ visible, onHide, contasPagar, onConfirm }: Props) {

    const { msgErro, msgSucesso } = useToastContext();
    const { startLoading, stopLoading } = useLoadingContext();
    const [novaDataVencimento, setNovaDataVencimento] = useState<Date | null>(null);
    const [simulacaoResultados, setSimulacaoResultados] = useState<SimulacaoAntecipacaoSiengeResponse | null>(null);


    const handleConfirm = () => {
        if (!novaDataVencimento) {
            msgErro("Data inválida", "Informe uma nova data de vencimento.");
            return;
        }
        
        const dados = new SolicitarAntecipacaoRequest(novaDataVencimento, contasPagar);

        antecipacaoParcelaService.solicitarAntecipacao(dados).then(response => {
            msgSucesso("Antecipação solicitada com sucesso", "A antecipação das parcelas selecionadas foi solicitada com sucesso.");
            onConfirm();
        }).catch(err => {
            msgErro("Erro ao simular antecipação", "Ocorreu um erro ao tentar simular a antecipação das parcelas selecionadas.");
        });
    }

    const handleSimular = () => {
        if (!novaDataVencimento) {
            msgErro("Data inválida", "Informe uma nova data de vencimento para a simulação.");
            return;
        }
        var date = new Date();
        var ultimoDiaDoMes = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
        if(novaDataVencimento.getTime() > ultimoDiaDoMes.getTime()) {
            msgErro("Data inválida", "A nova data de vencimento deve ser no mês atual.");
            return;
        }
        if (contasPagar.some(contaPagar => localDateStringToDate(contaPagar.vencimento).getTime() < novaDataVencimento.getTime())) {
            msgErro("Data inválida", "A nova data de vencimento deve ser igual ou menor que a data de vencimento atual das parcelas selecionadas.");
            return;
        }
        const dados = new SimularAntecipacaoRequest(novaDataVencimento, contasPagar.map(contaPagar => new ParcelaAntecipacaoSienge(contaPagar.billReceivableId, contaPagar.installmentId)));
        startLoading();
        antecipacaoParcelaService.simular(dados).then(response => {
            stopLoading();
            setSimulacaoResultados(response);
        }).catch(err => {
            stopLoading();
            msgErro("Erro ao simular antecipação", err?.data?.message || "Ocorreu um erro ao tentar simular a antecipação das parcelas selecionadas.");
        });
    }

    const getContaPagarByInstallmentId = (installmentId: number): ContaPagar | undefined => {
        return contasPagar.find(contaPagar => contaPagar.installmentId === installmentId);
    }

    return (
        <Dialog
            header={simulacaoResultados ? "Antecipação de parcela" : "Selecione uma data para simular a antecipação"}
            visible={visible}
            style={{ width: "80%" }}
            onHide={() => onHide()}
            footer={
                <div>
                    <Button
                        label="Cancelar"
                        icon="pi pi-times"
                        outlined
                        className="p-button-text"
                        onClick={() => onHide()}
                    />
                    {simulacaoResultados ? <Button
                        label="Solicitar antecipação"
                        icon="pi pi-check"
                        onClick={() => handleConfirm()}
                    /> : null}
                </div>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                <div>
                    <span>Data de vencimento</span>
                    <Calendar
                        disabled={!!simulacaoResultados}
                        value={novaDataVencimento}
                        onChange={(e) => setNovaDataVencimento(e.value as Date)}
                        dateFormat="dd/mm/yy"
                        showIcon
                        className="w-full"
                    />
                </div>
                <div style={{ alignSelf: 'flex-end' }}>
                    <Button
                        disabled={!!simulacaoResultados}
                        label="Simular"
                        onClick={() => handleSimular()}
                        className="p-button-sm p-button-outlined"
                    />
                </div>
                {!!simulacaoResultados ? <div style={{ alignSelf: 'flex-end' }}>
                    <Button
                        label="Refazer Simulação"
                        onClick={() => setSimulacaoResultados(null)}
                        className="p-button-sm p-button-outlined"
                    />
                </div> : null}
            </div>

            {!!simulacaoResultados &&
                <div style={{ marginTop: '16px' }}>
                    <strong>Resumo da Simulação</strong>

                    <div>Total: {contasPagar.map(contaPagar => contaPagar.valorAtual).reduce((a, b) => a + b).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                    })}</div>

                    <div>Total com desconto: {simulacaoResultados.amount.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                    })}</div>

                    <span style={{ marginTop: 16 }}>As parcelas serão agrupadas em um único boleto, com o valor final atualizado com os descontos.</span>
                </div>
            }

            <DataTable value={simulacaoResultados?.installments || []} className="mt-4" emptyMessage="Nenhuma parcela simulada">
                <Column header="Parcela" body={(rowData: ParcelaSimulacaoAntecipacaoSiengeResponse) => getContaPagarByInstallmentId(rowData.installmentId)?.numeroParcela} />
                <Column header="Novo Valor" field="currentAmount" body={(rowData: ParcelaSimulacaoAntecipacaoSiengeResponse) => rowData.installmentValue.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                })} />
            </DataTable>

        </Dialog>
    )
}